import React from "react";
import { Loader2, Shield } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { useAuthMe } from "../../hooks/useAuthMe";
import { useMergedCloudflareApplications } from "../../hooks/useMergedCloudflareApplications";
import {
  findEmbedAppByPathSegment,
  isSamePageEmbedLoop,
  isSelfReferentialEmbedUrl,
} from "../../utils/embeddedAppPaths";

interface CloudflareRouteState {
  externalUrl?: string;
  tabName?: string;
  subTab?: string;
}

function PremiumFeatureCard(props: { tabName: string; subTab: string }): React.ReactElement {
  const category = props.tabName.trim() || "Module";
  const feature = props.subTab.trim() || "Application";

  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center px-4 py-10 sm:px-6">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200/90 bg-white/95 px-6 py-8 text-center shadow-[0_1px_0_rgba(15,23,42,0.06),0_12px_40px_-12px_rgba(15,23,42,0.12)] dark:border-slate-700/80 dark:bg-[#141a26]/95 dark:shadow-[0_12px_48px_-16px_rgba(0,0,0,0.45)]">
        <div
          className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200/80 bg-slate-50 text-slate-600 dark:border-slate-600/80 dark:bg-slate-800/80 dark:text-slate-300"
          aria-hidden
        >
          <Shield className="h-6 w-6" strokeWidth={1.75} />
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">Not set up yet</p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-50">{feature}</h2>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{category}</p>
        <div className="mx-auto mt-6 flex max-w-[19rem] flex-col gap-2 text-center text-sm leading-snug text-slate-600 dark:text-slate-300">
          <p>This module is a premium add-on and is not enabled for your account yet.</p>
          <p>Your organisation can purchase or upgrade your plan to include it.</p>
          <p>After that, your VariPhi administrator can switch it on and add your workspace link.</p>
          <p>For billing or access questions, contact your VariPhi administrator.</p>
        </div>
      </div>
    </div>
  );
}

function EmbedLoadingState(): React.ReactElement {
  return (
    <div
      className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white p-8 text-slate-600 dark:border-slate-700 dark:bg-[#1C202F]/70 dark:text-slate-300"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2 className="h-9 w-9 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Loading application…</p>
    </div>
  );
}

function InvalidEmbedUrlCard(props: { subTab: string; samePageLoop?: boolean }): React.ReactElement {
  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-xl border border-amber-200 bg-amber-50/80 p-6 text-center dark:border-amber-500/30 dark:bg-amber-950/20">
      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
        Cannot embed &ldquo;{props.subTab}&rdquo; inside this portal
      </p>
      <p className="mt-2 max-w-md text-sm text-amber-800/90 dark:text-amber-200/90">
        {props.samePageLoop ? (
          <>
            The configured URL points to this same VariPhi page (
            <code className="rounded bg-amber-100/80 px-1 py-0.5 text-xs dark:bg-amber-900/50">
              /embed/…
            </code>
            ), which nests the portal instead of your external workspace. Ask your administrator to set
            the Cloudflare Access or external application URL.
          </>
        ) : (
          <>
            The configured URL points back to this VariPhi app (for example{" "}
            <code className="rounded bg-amber-100/80 px-1 py-0.5 text-xs dark:bg-amber-900/50">
              /embed/…
            </code>
            ). Your administrator must set the external Cloudflare Access application URL instead.
          </>
        )}
      </p>
    </div>
  );
}

const CloudflareApplicationFrame: React.FC = () => {
  const { appEmbed = "" } = useParams();
  const location = useLocation();
  const routeState = (location.state || {}) as CloudflareRouteState;
  const authQuery = useAuthMe();
  const cloudflareApps = useMergedCloudflareApplications();

  const selectedApp = React.useMemo(
    () => findEmbedAppByPathSegment(cloudflareApps, appEmbed),
    [cloudflareApps, appEmbed],
  );

  const iframeUrl =
    (selectedApp?.url ?? "").trim() || (routeState.externalUrl ?? "").trim();
  const tabName = selectedApp?.tab || routeState.tabName || "App";
  const subTab = selectedApp?.subTab || routeState.subTab || "Application";
  const hasEmbedTarget = Boolean(selectedApp) || Boolean(iframeUrl);
  const samePageLoop = Boolean(iframeUrl && isSamePageEmbedLoop(iframeUrl, appEmbed));

  if (!hasEmbedTarget) {
    if (authQuery.isPending) {
      return <EmbedLoadingState />;
    }
    return (
      <div className="h-full rounded-xl border border-slate-200 bg-white p-6 text-slate-700 dark:border-slate-700 dark:bg-[#1C202F]/70 dark:text-slate-200">
        Unable to load this application. Please select a valid sub-tab from the sidebar.
      </div>
    );
  }

  if (!iframeUrl) {
    return <PremiumFeatureCard tabName={tabName} subTab={subTab} />;
  }

  if (isSelfReferentialEmbedUrl(iframeUrl) || samePageLoop) {
    return <InvalidEmbedUrlCard subTab={subTab} samePageLoop={samePageLoop} />;
  }

  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  return (
    <div
      className={`flex h-full flex-col overflow-hidden bg-white dark:bg-[#101728] ${
        inIframe ? "min-h-screen" : "rounded-xl border border-slate-200 dark:border-slate-700"
      }`}
    >
      <iframe
        title={`${tabName}-${subTab}`}
        src={iframeUrl}
        className="h-full min-h-0 w-full flex-1 border-0"
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-read; clipboard-write; fullscreen"
      />
    </div>
  );
};

export default CloudflareApplicationFrame;
